// Tabla de Informes rápidos que cabe en la pantalla: calcula cuántas filas
// entran en el alto que le toca (ResizeObserver) y pagina el resto. El
// encabezado queda fijo y lo que no cabe a lo ancho hace scroll adentro. Las
// descargas (ExportButtons) siguen recibiendo TODAS las filas, no la página.
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { ReportTableColumn } from "../reports/components/ReportPointsTable";
import { fitRows, pageInfo, pageList } from "./pagination";

export type PagedColumn<T> = ReportTableColumn<T> & { render?: (row: T) => ReactNode };

// Alto fijo de fila y de encabezado (h-8 / h-10, celdas sin salto de línea):
// así las filas que caben salen de una división, sin medir el DOM.
const ROW_PX = 32;
const HEAD_PX = 40;

export function usePagination<T>(data: T[], size: number) {
  const [page, setPage] = useState(0);
  // Datos nuevos (otro filtro, otro periodo): de vuelta a la primera página.
  useEffect(() => {
    setPage(0);
  }, [data]);
  const info = pageInfo(data.length, size, page);
  return { ...info, rows: data.slice(info.start, info.end), list: pageList(info.page, info.pages), setPage };
}

// Filas que caben en el elemento observado; mientras no se mide, el mínimo.
function useFitRows<E extends HTMLElement>() {
  const ref = useRef<E>(null);
  const [rows, setRows] = useState(5);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setRows(fitRows(el.clientHeight, ROW_PX, HEAD_PX)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, rows] as const;
}

export default function PagedTable<T extends { id: string }>({
  data,
  columns,
  isDarkMode,
  emptyText = "Sin datos",
  className = "",
}: {
  data: T[];
  columns: PagedColumn<T>[];
  isDarkMode: boolean;
  emptyText?: string;
  className?: string;
}) {
  const [boxRef, size] = useFitRows<HTMLDivElement>();
  const p = usePagination(data, size);
  const dark = isDarkMode;
  const muted = dark ? "text-gray-400" : "text-gray-500";
  const cell = dark ? "text-white" : "text-gray-900";
  const pageBtn = `min-w-8 h-8 px-2 rounded-lg text-xs tabular-nums transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-40 disabled:pointer-events-none ${
    dark ? "hover:bg-white/10" : "hover:bg-black/5"
  }`;

  return (
    // Móvil: alto fijo de 70dvh (la página hace scroll). Escritorio: el alto que
    // sobra en el panel del informe (flex-1), nunca menos que 5 filas.
    <div className={`flex flex-col gap-2 min-w-0 h-[70dvh] min-h-[16rem] lg:h-auto lg:flex-1 ${className}`}>
      <div ref={boxRef} className={`flex-1 min-h-0 overflow-auto rounded-xl border ${dark ? "border-dark-border" : "border-gray-200"}`}>
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10">
            <tr className="h-10">
              {columns.map((c) => (
                <th
                  key={String(c.key)}
                  scope="col"
                  className="px-3 font-semibold whitespace-nowrap bg-primary-500 text-white"
                  style={{ textAlign: c.align || "left" }}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr className="h-8">
                <td colSpan={columns.length} className={`px-3 text-center ${muted}`}>
                  {emptyText}
                </td>
              </tr>
            ) : (
              p.rows.map((row) => (
                <tr key={row.id} className={`h-8 ${dark ? "odd:bg-[#232323] even:bg-[#1C1C1C]" : "odd:bg-gray-50 even:bg-white"}`}>
                  {columns.map((c) => (
                    <td key={String(c.key)} className={`px-3 whitespace-nowrap ${cell}`} style={{ textAlign: c.align || "left" }}>
                      {c.render ? c.render(row) : String(row[c.key] ?? "")}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <nav aria-label="Páginas" className="flex items-center justify-between gap-3 flex-shrink-0">
        <span className={`text-xs tabular-nums ${muted}`} aria-live="polite">
          {p.label}
        </span>
        {p.pages > 1 && (
          <div className={`flex items-center gap-0.5 ${cell}`}>
            <button type="button" className={pageBtn} onClick={() => p.setPage(p.page - 1)} disabled={p.page === 0} aria-label="Página anterior">
              <i className="ri-arrow-left-s-line text-base leading-none" aria-hidden />
            </button>
            {p.list.map((n, i) =>
              n === "…" ? (
                <span key={`gap-${i}`} className={`px-1 text-xs ${muted}`} aria-hidden>
                  …
                </span>
              ) : (
                <button
                  key={n}
                  type="button"
                  aria-current={n === p.page ? "page" : undefined}
                  onClick={() => p.setPage(n)}
                  className={`${pageBtn} ${n === p.page ? "bg-primary-500/15 font-semibold text-primary-500" : ""}`}
                >
                  {n + 1}
                </button>
              ),
            )}
            <button type="button" className={pageBtn} onClick={() => p.setPage(p.page + 1)} disabled={p.page >= p.pages - 1} aria-label="Página siguiente">
              <i className="ri-arrow-right-s-line text-base leading-none" aria-hidden />
            </button>
          </div>
        )}
      </nav>
    </div>
  );
}
